import ReactDatePicker from 'react-datepicker'
import './style.scss'
import { InputBase, styled } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import * as d3 from 'd3'
import { completeDateString } from '../../../commons/inputSupporter'
import _ from 'lodash'
import { colorPalette } from '../../../style/colorPalette'
import { Portal } from 'react-overlays'
import { dateV2Service } from '../../../../domain/value-object/DateV2'

type DatePickerProps = {
  value: Date | undefined
  onChange: (v: Date) => void
  // This interval is open-closed interval, which means the start is not included, but the end included.
  includeDateIntervals?: [{ start: Date; end: Date }]
}
type DatePickerPopupProps = DatePickerProps
export const DatePicker = ({
  value,
  onChange: _onChange,
  ...others
}: DatePickerProps) => {
  const onChange = useCallback(
    (v: Date) => {
      const value = d3.timeDay.floor(v)
      _onChange(value)
    },
    [_onChange]
  )
  return (
    <ReactDatePicker
      selected={value}
      onChange={onChange}
      inline={true}
      {...others}
    />
  )
}

type DateInputProps = {
  value?: Date
  initialValueOnCalendar?: Date
  onChange: (value?: Date) => void
  placeholder?: string
  editable?: boolean
  popperPlacement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'
  minDate?: Date
}
export const DateInput = ({
  value,
  initialValueOnCalendar,
  onChange,
  placeholder,
  editable = true,
  popperPlacement = 'bottom-end',
  minDate,
}: DateInputProps) => {
  const [changedByInput, setChangedByInput] = useState(false)
  const [calendarOpened, setCalendarOpened] = useState(false)
  const maxDate = useMemo(() => dateV2Service.maxDate(), [])

  useEffect(() => {
    setChangedByInput(false)
  }, [value])

  const onSelectCallback = useCallback(
    (value?: Date) => {
      if (!changedByInput) {
        onChange(value)
      } else {
        setChangedByInput(false)
      }
    },
    [onChange, changedByInput]
  )
  const onChangeCaptureCallback = useCallback(
    _.debounce(event => {
      const inputValue = event.target.value
      setChangedByInput(true)

      if (!inputValue) {
        // 値をクリアした時
        onChange(undefined)
      }
      const value = completeDateString(inputValue)
      if (!value) {
        return
      }
      const newValue = new Date(value.replace(/\//g, '-'))
      onChange(newValue)
    }, 200),
    [changedByInput, onChange]
  )

  return (
    <ReactDatePicker
      selected={value || (calendarOpened ? initialValueOnCalendar : undefined)}
      dateFormat={'yyyy/MM/dd'}
      onSelect={onSelectCallback}
      placeholderText={placeholder || 'YYYY/MM/DD'}
      customInput={
        <TextInput
          onChangeCapture={onChangeCaptureCallback}
          onFocusCapture={() => setCalendarOpened(true)}
          onBlurCapture={() => setCalendarOpened(false)}
        />
      }
      popperPlacement={popperPlacement}
      disabled={!editable}
      onKeyDown={e => {
        if (e.key === 'Escape') {
          e.stopPropagation()
          e.currentTarget.blur()
        }
      }}
      fixedHeight={true}
      popperContainer={CalendarContainer}
      minDate={minDate}
      maxDate={maxDate}
    />
  )
}
const CalendarContainer = ({ children }) => {
  return <Portal container={null}>{children}</Portal>
}

const TextInput = styled(InputBase)({
  width: '100%',
  color: colorPalette.monotone[10],
})
